<template>
  <div class="directory-company-detail">
    <div
      v-if="showUpdateInfo"
      class="last-update"
    >
      <div class="last-update__text">
        {{ updateInfo }}
      </div>
    </div>
    <CompanyInfo
      :detail="company"
      :index="index"
      :id="'main-comp' + index"
      :showIndex="false"
      :withDropdown="false"
      :resetValidation="_resetValidation"
      :category="['companies']"
      :type="type"
      class="main-comp"
      :canCompInfoEditable="canCompInfoEditableForEngineer"
      :title="$t('directory.label.level1')"
    />
    <hr>
    <div
      class="sub-company"
      v-if="subCompLength"
    >
      <template v-for="(subComp, subIndex) in company.subCompanies">
        <CompanyInfo
          :detail="subComp"
          :index="subIndex"
          :lengthOfArray="company.subCompanies.length"
          :resetValidation="_resetValidation"
          :category="['companies', index, 'subCompanies']"
          :type="type"
          :id="index + '-sub-Comp-' + subIndex"
          :key="subIndex"
          :isSub="true"
          :title="$t('directory.label.level2')"
          :withDropdown="canCompInfoEditableForEngineer"
          :canCompInfoEditable="canCompInfoEditableForEngineer"
        />
        <div
          class="comp-detail"
          :key="subIndex"
        >
          <template v-for="(subDetail, detailIndex) in subComp.subDetails">
            <CompanyInfo
              :detail="subDetail"
              :index="detailIndex"
              :lengthOfArray="subComp.subDetails.length"
              :key="detailIndex"
              :isDetail="true"
              :resetValidation="_resetValidation"
              :category="['companies', index, 'subCompanies', subIndex, 'subDetails']"
              :id="index + '-sub-Comp-' + subIndex + '-sub-detail-' + detailIndex"
              :type="type"
              :title="$t('directory.label.level3')"
              :withDropdown="canCompInfoEditableForEngineer"
              :canCompInfoEditable="canCompInfoEditableForEngineer"
            />
          </template>
          <div
            class="buttons"
            :key="subIndex"
            v-if="canCompInfoEditable"
          >
            <a
              class="add-comp"
              @click="addCompanyDetail(subComp, subIndex)"
            >{{ $t('action.addCompDetail') }}</a>
          </div>
        </div>
        <hr
          class="indent"
          v-if="subCompLength > 0"
          :key="subIndex"
        >
      </template>
    </div>
    <div
      class="buttons"
      v-if="canCompInfoEditable"
    >
      <button
        type="button"
        class="btn btn--round btn--grey"
        @click="addSubComp"
      >{{ $t('action.addSubComp') }}</button>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapMutations, mapGetters } from 'vuex'

import CompanyInfo from './CompanyInfo'
import directoryCompanyMixin from './directoryCompanyMixin'

import dateUtil from '@/utils/dateUtil'
import mutationTypes from '@/store/mutation-types'
import { creatorEditableStatus, DIR_COMP_TYPE, DIR_ACTION, DIR_STATUS, tenantCreatorEditableStatus } from './consts'
import { utils as formUtils } from '@/plugin/InputChange'

export default {
  name: 'DirectoryCompanyDetail',
  components: {
    CompanyInfo,
  },
  mixins: [directoryCompanyMixin],
  props: {
    id: {
      type: String,
    },
    index: {
      type: Number,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    company: {
      type: Object,
      default: () => {},
    },
  },

  methods: {
    ...mapActions('directories', ['loadDirectory']),
    ...mapMutations('directories', {
      updateField: mutationTypes.DIRECTORY_SET_FIELD_BY_PATH,
      updateCompany: mutationTypes.DIRECTORY_UPDATE_COMPANY,
    }),

    _updateField (path, value) {
      this.updateField({ path, value })
    },

    _resetValidation () {
      this.$emit('reset:validation')
    },

    addSubComp () {
      this.updateCompany({
        action: 'ADD',
        parentId: this.company.id,
        path: ['companies', this.index, 'subCompanies'],
        type: DIR_COMP_TYPE.SUB,
      })
      formUtils.setDirty()
    },

    addCompanyDetail (subComp, subIndex) {
      this.updateCompany({
        action: 'ADD',
        parentId: subComp.id,
        path: ['companies', this.index, 'subCompanies', subIndex, 'subDetails'],
        type: DIR_COMP_TYPE.DETAIL,
      })
      formUtils.setDirty()
    },
  },

  computed: {
    ...mapGetters('user', ['isTenant', 'isEngineer']),
    ...mapGetters('directories', ['currentDirectory', 'canCurrUserApprove', 'canCurrUserCreate']),
    subCompLength () {
      return lodash.get(this.company, 'subCompanies.length')
    },
    canCompInfoEditable () {
      const isEditableStatus = tenantCreatorEditableStatus.includes(this.currentDirectory.status)
      const isNotRemoveAction = this.currentDirectory.action !== DIR_ACTION.REMOVE
      return !this.isViewPage && this.canCurrUserCreate && isNotRemoveAction && isEditableStatus
    },
    canCompInfoEditableForEngineer () {
      const isEditableStatus = creatorEditableStatus.includes(this.currentDirectory.status)
      const isNotRemoveAction = this.currentDirectory.action !== DIR_ACTION.REMOVE
      return !this.isViewPage && this.canCurrUserCreate && isNotRemoveAction && isEditableStatus
    },
    showUpdateInfo () {
      const canShowUpdateInfo = [DIR_STATUS.SUBMITTED, DIR_STATUS.APPR]
      return this.isEngineer && this.canCurrUserCreate && this.currentDirectory.action !== DIR_ACTION.REMOVE &&
        canShowUpdateInfo.includes(this.currentDirectory.status) && this.updateInfo
    },
    updateInfo () {
      const histories = lodash.orderBy(lodash.get(this, 'currentDirectory.histories', []), 'createdAt').reverse()
      let updaterDisplayName = ''
      let updatedAt = ''
      let statusFullName = ''
      let status = ''
      let action = ''

      histories.some(history => {
        if (history.status === 'SAVE_INFO') {
          updaterDisplayName = lodash.get(history, 'who.displayName', '')
          updatedAt = dateUtil.formatDate(history.createdAt, true)
          const currentStatus = lodash.get(history, 'detail.currentStatus', '')
          if (currentStatus === DIR_STATUS.SUBMITTED) {
            status = DIR_STATUS.WAPPR
          }
          if (currentStatus === DIR_STATUS.APPR) {
            status = DIR_STATUS.APPR
          }
          action = lodash.get(history, 'detail.action', '')
          return true
        }
      })
      statusFullName = `${status}(${action})`
      return updaterDisplayName && this.$t('directory.msg.latestSaveInfo', [updaterDisplayName, updatedAt, statusFullName])
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/common';
@import '~styles/directories/common';

.directory-company-detail {
  background-color: #ffffff;
  box-shadow: 0 2px 4px 0 rgba(212, 212, 212, 0.5);
  margin-top: 15px;
  padding: 20px 20px 10px;

  .last-update {
    margin-bottom: 5px;
  }

  .row.title {
    background-color: var(--twilight-blue);
  }

  .buttons {
    padding: 15px 0;
    text-align: center
  }

  .columns {
    padding-right: 10px;
  }

  .sub-company {
    padding-left: 10px;
    .company-info {
      box-shadow: none;
    }
    .indent {
      padding: 0 15px;
    }
  }

  .comp-detail {
    .company-info {
      margin-top: 15px;
    }
    .buttons {
      padding: 10px 0 0;
      text-align: left;
    }
  }
}
</style>

<style lang="scss">
.directory-company-detail {

  .main-comp {
    .title {
      background-color: #0C3A58;
      label {
        color: #ffffff;
      }
    }
  }

  .comp-detail .company-info .title {
    border-bottom: #e6e6e6 1px solid;
    background-color: #ececec;
  }

  .input-field input {
    width: 100%!important;
  }
  .chinese-name .input-field label {
    margin-top: 0;
    font-size: 12px;
    color: var(--twilight-blue);
  }
}
</style>
